import React from "react";
import { useNavigate } from "react-router-dom";
import { Loading } from "src/components/Feedback/Loading";
import { connectStripeAccount } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useInvalidateOrgConfig } from "src/hooks/useOrgConfig";

export const StripeUpdateConfiguration: React.FC = () => {
  const organization = useOrganization();
  const navigate = useNavigate();
  const toast = useAvelaToast();
  const { scheduleOrgConfigInvalidation } = useInvalidateOrgConfig();

  React.useEffect(() => {
    const stripeCode = sessionStorage.getItem("stripe_oauth_code");

    if (organization.isLoading() || organization.isNotAsked()) return;

    if (organization.hasError()) {
      toast.error({
        title: "Organization not available",
      });
      navigate("/");
      return;
    }

    if (!stripeCode) {
      toast.error({
        title: "No authorization code found",
      });
      navigate(
        organization.hasData()
          ? `/${
              organization.data?.path || "demo"
            }/admin/organization/settings#tools`
          : "/"
      );
      return;
    }

    async function processStripeCode() {
      if (!organization.hasData() || !stripeCode) return;

      try {
        await connectStripeAccount({
          path: {
            organizationId: organization.data.id,
          },
          body: {
            code: stripeCode,
          },
        });

        sessionStorage.removeItem("stripe_oauth_code");
        toast({
          title: "Stripe account connected successfully",
          status: "success",
        });

        // flush org config cache for payments
        // we wait for 5s before we invalidate org config to allow CDN cache invalidation to finish.
        // it's not 100% guarantee to work all the time, but I think it's the best we can do
        scheduleOrgConfigInvalidation("Payments", { delayInMs: 5000 });

        navigate(
          `/${organization.data.path}/admin/organization/settings#tools`
        );
      } catch (err) {
        toast.error({
          title: "Failed to connect Stripe account",
          description:
            err instanceof Error ? err.message : "An unexpected error occurred",
        });
        navigate(
          `/${organization.data.path}/admin/organization/settings#tools`
        );
      }
    }

    processStripeCode();
  }, [organization, navigate, toast, scheduleOrgConfigInvalidation]);

  return <Loading />;
};
